import { AnyToken, NFT } from 'src/common/types/models/Tokens'
import { big, normalize } from 'src/common/utils/big-utils'
import { useReadContract } from 'thirdweb/react'
import { balanceOf as balanceOfERC20 } from 'thirdweb/extensions/erc20'
import { balanceOf as balanceOfERC721 } from 'thirdweb/extensions/erc721'
import { balanceOf as balanceOfERC1155 } from 'thirdweb/extensions/erc1155'
import useContract from '../common/useContract/useContract'
import { useMemo } from 'react'

type UseBalanceParams = {
  token:
    | Pick<AnyToken, 'contractAddress' | 'type' | 'decimals'>
    | undefined
    | null
  owner: string | undefined // The address whose balance to fetch
}

const useBalance = ({ token, owner = '' }: UseBalanceParams) => {
  const { contract } = useContract(token?.contractAddress || '')

  const areParametersValid = owner && token && token?.contractAddress
  const isERC20 = Boolean(areParametersValid && token.type === 'ERC20')
  const isERC721 = Boolean(areParametersValid && token.type === 'ERC721')
  const isERC1155 = Boolean(areParametersValid && token.type === 'ERC1155')

  const {
    data: erc20Balance,
    isLoading: isErc20Loading,
    error: erc20Error,
    refetch: refetchErc20,
    isRefetching: isErc20Refetching,
  } = useReadContract(balanceOfERC20, {
    contract,
    address: owner,
    queryOptions: { enabled: isERC20 },
  })

  const {
    data: erc721Balance,
    isLoading: isErc721Loading,
    error: erc721Error,
    refetch: refetchErc721,
    isRefetching: isErc721Refetching,
  } = useReadContract(balanceOfERC721, {
    contract,
    owner,
    queryOptions: {
      enabled: isERC721,
    },
  })

  const {
    data: erc1155Balance,
    isLoading: isErc1155Loading,
    error: erc1155Error,
    refetch: refetchErc1155,
    isRefetching: isErc1155Refetching,
  } = useReadContract(balanceOfERC1155, {
    contract,
    owner,
    tokenId: BigInt((token as NFT)?.tokenId || 0),
    queryOptions: {
      enabled: isERC1155,
    },
  })

  const balance = useMemo(() => {
    if (!areParametersValid) return null
    const type = token.type
    switch (type) {
      case 'ERC20':
        return normalize(erc20Balance?.toString() || '0', token.decimals)
      case 'ERC721':
        return big(erc721Balance?.toString())
      case 'ERC1155':
        return big(erc1155Balance?.toString())
      default:
        return null
    }
  }, [
    areParametersValid,
    erc1155Balance,
    erc20Balance,
    erc721Balance,
    token?.decimals,
    token?.type,
  ])

  const refetch = useMemo(() => {
    if (!areParametersValid) return null
    const type = token.type
    switch (type) {
      case 'ERC20':
        return refetchErc20
      case 'ERC721':
        return refetchErc721
      case 'ERC1155':
        return refetchErc1155
      default:
        return null
    }
  }, [
    areParametersValid,
    refetchErc1155,
    refetchErc20,
    refetchErc721,
    token?.type,
  ])

  const isLoading = isErc20Loading || isErc721Loading || isErc1155Loading
  const isRefetching =
    isErc20Refetching || isErc721Refetching || isErc1155Refetching
  const error = erc20Error || erc721Error || erc1155Error

  return { balance, isLoading, isRefetching, error, refetch }
}

export default useBalance
